<script setup lang="ts">
import { ref, onMounted } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import ServicesSection from './sections/ServicesSection.vue'
import ProjectsSection from './sections/ProjectsSection.vue'
import FooterSection from './sections/FooterSection.vue'
import { projectsData } from './data/projects'

gsap.registerPlugin(ScrollTrigger)

const isInServicesSection = ref(false)
const isInFooterSection = ref(false)
const isMenuActive = ref(false)
const isHovering = ref(false)
const isMobile = ref(false)

const scrollToSection = (id: string) => {
  if (id === 'home') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const handleMenuClick = (section: string) => {
  if (isMobile.value) isMenuActive.value = false
  scrollToSection(section)
}

const handleMenuMouseLeave = () => {
  isHovering.value = false
  if (isMobile.value) isMenuActive.value = false
}

onMounted(() => {
  isMobile.value = window.innerWidth < 768
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth < 768
  })

  ScrollTrigger.getAll().forEach((t) => t.kill())

  // 2. Services section pin
  ScrollTrigger.create({
    trigger: '#services',
    start: 'top top',
    endTrigger: '#work',
    end: 'top top',
    pin: true,
    pinSpacing: false,
  })

  // 3. Logo color: invert when over services section
  const servicesLogoTrigger = ScrollTrigger.create({
    trigger: '#services',
    start: 'top 60px',
    end: 'bottom 60px',
    onEnter: () => { isInServicesSection.value = true },
    onLeave: () => { isInServicesSection.value = false },
    onEnterBack: () => { isInServicesSection.value = true },
    onLeaveBack: () => { isInServicesSection.value = false },
  })
  // Services now sits at the top of the page, so seed the initial state
  isInServicesSection.value = servicesLogoTrigger.isActive

  // 4. Footer: show nav links when in footer
  ScrollTrigger.create({
    trigger: '#contact',
    start: 'top 50%',
    end: 'bottom bottom',
    onEnter: () => { isInFooterSection.value = true; isMenuActive.value = !isMobile.value },
    onLeave: () => { isInFooterSection.value = true; isMenuActive.value = !isMobile.value },
    onEnterBack: () => { isInFooterSection.value = true; isMenuActive.value = !isMobile.value },
    onLeaveBack: () => { isInFooterSection.value = false; isMenuActive.value = false },
  })

  // 5. Project video scroll parallax
  const projectSections = document.querySelectorAll('.grid')
  projectSections.forEach((section, index) => {
    const videoContainer = section.querySelector('.project-video-container') as HTMLElement
    const content = section.querySelector('.project-content') as HTMLElement

    if (videoContainer && content) {
      gsap.set(videoContainer, { y: 0 })
      const multiplier = projectsData[index]?.scrollMultiplier ?? 0.42

      ScrollTrigger.create({
        trigger: content,
        start: 'top center',
        end: 'bottom center',
        scrub: true,
        onUpdate: (self) => {
          const maxMove = content.offsetHeight * multiplier
          gsap.set(videoContainer, { y: Math.min(self.progress * maxMove, maxMove) })
        },
      })
    }
  })
})
</script>

<template>
  <div class="min-h-screen bg-[#151515] text-white overflow-x-hidden">
    <!-- Fixed navigation -->
    <div class="fixed top-2 left-1/2 -translate-x-1/2 z-50 w-fit h-12 group">
      <img
        src="/deepcuts-logo.svg"
        alt="DeepCuts"
        @click="isMenuActive = !isMenuActive"
        class="logo-svg h-12 max-w-[180px] transition-all duration-300 cursor-pointer md:cursor-default"
        :class="{
          'invert': isInServicesSection || isInFooterSection,
          'opacity-0 translate-y-2': isMenuActive || (isHovering && !isMobile),
        }"
      />
      <nav
        class="absolute top-0 left-1/2 -translate-x-1/2 transition-opacity duration-300 z-10"
        @mouseenter="isHovering = true"
        @mouseleave="handleMenuMouseLeave"
        :class="{
          'opacity-0 pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto': !isMenuActive && !isInFooterSection,
          'opacity-100 pointer-events-auto': isMenuActive || (isInFooterSection && !isMobile),
          'opacity-0 pointer-events-none': isInFooterSection && isMobile,
        }"
      >
        <div class="flex flex-row text-[11px] mono tracking-[0.01em] mt-2">
          <button @click="handleMenuClick('home')" class="bg-white/30 backdrop-blur-sm hover:bg-white/35 rounded-lg px-3 py-[6px] mr-4 text-black flex items-center justify-center min-w-[60px] pt-2">HOME</button>
          <button @click="handleMenuClick('services')" class="bg-white/30 backdrop-blur-sm hover:bg-white/35 rounded-lg px-3 py-[6px] mr-4 text-black flex items-center justify-center min-w-[60px] pt-2">SERVICES</button>
          <button @click="handleMenuClick('work')" class="bg-white/30 backdrop-blur-sm hover:bg-white/35 rounded-lg px-3 py-[6px] mr-4 text-black flex items-center justify-center min-w-[60px] pt-2">WORK</button>
          <button @click="handleMenuClick('contact')" class="bg-white/30 backdrop-blur-sm hover:bg-white/35 rounded-lg px-3 py-[6px] text-black flex items-center justify-center min-w-[60px] pt-2">CONTACT</button>
        </div>
      </nav>
    </div>

    <ServicesSection :is-mobile="isMobile" />
    <ProjectsSection />
    <FooterSection />
  </div>
</template>

<style>
@import './assets/main.css';

/* Hide video play button on Safari */
video::-webkit-media-controls {
  display: none !important;
}
video::-webkit-media-controls-start-playback-button {
  display: none !important;
  -webkit-appearance: none;
}

body {
  @apply bg-black;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* Disable scroll animation on mobile */
@media (max-width: 767px) {
  .project-video-container {
    transform: none !important;
  }
}
</style>
